import { useEffect, useState } from "react";
import { MapPin, CircleAlert, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useStore } from "@/context/StoreContext";
import { api } from "@/lib/api";
import { DeliveryRules } from "@/components/store/DeliveryRules";
import { matchAreas } from "@/lib/areaSearch";
import { waLink } from "@/components/store/WhatsApp";
import { NotifyMeForm } from "@/components/store/NotifyMeForm";

const visitorId = () => {
  let v = localStorage.getItem("mf_visitor");
  if (!v) { v = crypto.randomUUID(); localStorage.setItem("mf_visitor", v); }
  return v;
};
const logDemand = (query) => { if (query.trim().length >= 2) api.post("/area-demand", { query, visitor_id: visitorId() }).catch(() => {}); };

export const PincodeDialog = () => {
  const { area, setArea, areaOpen, setAreaOpen, config } = useStore();
  const [areas, setAreas] = useState([]);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [unserved, setUnserved] = useState("");

  useEffect(() => { api.get("/areas").then((r) => setAreas(r.data)); }, []);
  useEffect(() => { if (!area) setAreaOpen(true); }, [area, setAreaOpen]);

  const check = async (code) => {
    setError("");
    const r = await api.get("/areas/check", { params: { pincode: code } });
    if (r.data.serviceable) { setArea(r.data.area); setAreaOpen(false); setPin(""); }
    else { setError(`We don't deliver to ${code} yet.`); logDemand(code); setUnserved(code); }
  };

  const matches = matchAreas(areas, pin);
  const submit = () => {
    const q = pin.trim();
    if (/^\d{6}$/.test(q)) return check(q);
    if (!q) return setError("Enter your pincode or area name");
    if (matches.length === 1) return check(matches[0].pincode);
    if (!matches.length) { logDemand(q); setUnserved(q); }
    setError(matches.length ? "Your area has more than one pincode. Please tap the right one below." : `We don't deliver to "${q}" yet.`);
  };
  const noMatch = pin.trim() && matches.length === 0;

  return (
    <Dialog open={areaOpen} onOpenChange={(o) => (area || !o ? setAreaOpen(o) : null)}>
      <DialogContent className="bg-white sm:max-w-md rounded-2xl" data-testid="pincode-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-primary">Where should we deliver?</DialogTitle>
          <DialogDescription>Same-day delivery in select Vadodara zones only.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex gap-2">
          <Input value={pin} onChange={(e) => { setPin(e.target.value.slice(0, 40)); setError(""); setUnserved(""); }} placeholder="Pincode or area, e.g. Gotri"
            className="h-12 rounded-full px-5 text-base" data-testid="pincode-input" />
          <button type="submit" className="h-12 px-6 rounded-full bg-primary text-white font-semibold active:scale-95 transition-transform" data-testid="pincode-check-btn">Check</button>
        </form>
        {(error || noMatch) && (
          <div className="flex gap-2 text-sm text-accent bg-accent/10 rounded-xl p-3" data-testid="pincode-error">
            <CircleAlert className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
            <span>{error || `We don't deliver to "${pin.trim()}" yet.`}{(noMatch || error.startsWith("We don't")) && config?.support_phone && (
              <> <a href={waLink(config.support_phone, `Hi Mukti Fresh, do you deliver to ${pin.trim()}?`)} target="_blank" rel="noreferrer" className="underline font-semibold" data-testid="pincode-ask-whatsapp">Ask us on WhatsApp</a></>
            )}</span>
          </div>
        )}
        {unserved && <NotifyMeForm key={unserved} query={unserved} />}
        <div>
          <p className="text-xs tracking-[0.1em] uppercase text-stone-500 font-bold mb-3">{pin.trim() ? `Matching zones (${matches.length})` : "Zones we serve"}</p>
          <div className="grid grid-cols-1 gap-2 max-h-64 overflow-auto" data-testid="area-options">
            {matches.map((a) => (
              <button key={a.id} onClick={() => check(a.pincode)} data-testid={`area-option-${a.pincode}`}
                className="flex items-center gap-3 text-left rounded-xl border border-stone-200 px-4 py-3 hover:border-primary/50 hover:bg-[#F9F8F4] transition-colors">
                <MapPin className="w-4 h-4 shrink-0 text-secondary" strokeWidth={1.5} />
                <span className="font-semibold text-primary">{a.pincode}</span>
                <span className="flex flex-col min-w-0">
                  <span className="text-sm text-stone-500">{a.name}</span>
                  <DeliveryRules area={a} />
                </span>
                {area?.pincode === a.pincode && <Check className="w-4 h-4 ml-auto text-secondary" />}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
